import { useState, useEffect } from 'react';
import { Activity, AlertTriangle, CheckCircle2, Upload, Settings, TrendingUp, TrendingDown, Bell, Mail, RefreshCw, Send, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import BulkActions from '../components/BulkActions';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SECTIONS } from '../lib/sectionTheme';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const G = SECTIONS.mbs; // Green

export default function MBSMonitorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_items: 0, items_changed: 0, unacknowledged_alerts: 0 });
  const [items, setItems] = useState([]);
  const [changes, setChanges] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [config, setConfig] = useState({ practice_manager_email: '', key_user_email: '' });
  const [tab, setTab] = useState('dashboard');
  const [showConfig, setShowConfig] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [s, i, c, a, cfg] = await Promise.all([
        axios.get(`${API}/mbs/stats`),
        axios.get(`${API}/mbs/items`),
        axios.get(`${API}/mbs/changes`),
        axios.get(`${API}/mbs/alerts`),
        axios.get(`${API}/mbs/config`)
      ]);
      setStats(s.data); setItems(i.data); setChanges(c.data); setAlerts(a.data); setConfig(cfg.data);
    } catch (e) { console.error('Failed to load MBS data'); }
  };

  const handleBulkUpload = async () => {
    const isPM = user?.role === 'admin' || user?.role === 'superuser';
    if (!isPM) { alert('Only Admin or Practice Manager can upload schedules.'); return; }
    setLoading(true);
    try {
      const lines = bulkInput.trim().split('\n').filter(l => l.trim());
      const parsedItems = lines.map(line => {
        const parts = line.split(',').map(p => p.trim());
        return { item_number: parts[0], description: parts[1] || '', schedule_fee: parseFloat(parts[2]) || 0, category: parts[3] || '' };
      });
      const res = await axios.post(`${API}/mbs/items/bulk`, parsedItems, { withCredentials: true });
      setBulkInput('');
      loadData();
      alert(`Uploaded ${res.data.count} items. ${res.data.changes_detected} changes detected.`);
    } catch (e) { alert('Upload failed. Please log in as admin.'); }
    setLoading(false);
  };

  const handleSaveConfig = async () => {
    try {
      await axios.post(`${API}/mbs/config`, config, { withCredentials: true });
      alert('Alert configuration saved');
      setShowConfig(false);
    } catch (e) { alert('Save failed. Admin login required.'); }
  };

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await axios.post(`${API}/mbs/sync`, {}, { withCredentials: true, timeout: 180000 });
      setSyncResult(r.data);
      if (r.data.status === 'success') {
        // Refresh dashboard data so new counts/alerts/changes appear immediately.
        await loadData();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Unknown error';
      setSyncResult({ status: 'failed', error: msg });
    }
    setSyncing(false);
  };

  const handleSendNotification = async () => {
    try {
      const r = await axios.post(`${API}/alerts/mbs-notify`, {}, { withCredentials: true });
      if (r.data.status === 'no_changes') alert('No fee changes to notify.');
      else alert(`Notifications sent to ${r.data.sent.length} recipient(s) (${r.data.sent[0]?.status || 'n/a'}).`);
    } catch { alert('Notification failed. Configure emails in Alert Settings first.'); }
  };

  const isPM = user?.role === 'admin' || user?.role === 'superuser';

  return (
    <div className="min-h-screen bg-background" data-testid="mbs-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header — title left, actions right (ReferralBase pattern) */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] mb-3" style={{ backgroundColor: G.colorBg, color: G.color, border: `1px solid ${G.ring}` }}>
              <Activity className="w-3 h-3" /> MBS Monitor
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-foreground" data-testid="mbs-title">Medicare Fee Schedule Monitor</h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">Track MBS updates, compare fees, and receive email alerts on changes.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <BulkActions resource="mbs_items" title="MBS Item Schedule" readOnly color={G.color} />
            {isPM && (
              <>
                <button data-testid="mbs-sync-btn" onClick={handleSync} disabled={syncing}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ borderColor: `${G.color}40`, color: G.color }}
                  title="Pull latest MBS schedule from www.mbsonline.gov.au and diff against stored items">
                  <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing…' : 'Sync now'}
                </button>
                <button data-testid="mbs-notify-btn" onClick={handleSendNotification} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border" style={{ borderColor: `${G.color}40`, color: G.color }} title="Email current fee-change alert to configured recipients">
                  <Send className="w-4 h-4" /> Send alerts
                </button>
              </>
            )}
            <button
              data-testid="mbs-config-btn"
              onClick={() => setShowConfig(!showConfig)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
              style={{ backgroundColor: G.color }}
            >
              <Settings className="w-4 h-4" /> Alert settings
            </button>
          </div>
        </div>

        {/* Stats Cards — always visible KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-testid="mbs-stats">
          <div className="bg-card border border-border rounded-xl p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total items</p>
            <p className="text-2xl font-bold mt-1 text-foreground">{stats.total_items}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Fee changes</p>
            <p className="text-2xl font-bold mt-1 text-amber-600">{stats.items_changed}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Unread alerts</p>
            <p className="text-2xl font-bold mt-1 text-red-600">{stats.unacknowledged_alerts}</p>
          </div>
        </div>

        {/* Chatbot hint */}
        <div data-testid="mbs-chat-hint"
          className="mb-6 rounded-xl border flex items-center gap-2 px-4 py-2.5"
          style={{ borderColor: `${G.color}40`, backgroundColor: `${G.color}08` }}>
          <Sparkles className="w-4 h-4 shrink-0" style={{ color: G.color }} />
          <p className="text-xs text-muted-foreground">
            Use the <span className="font-semibold" style={{ color: G.color }}>MBS Monitor Assist</span> chatbot in the bottom-right corner for instant answers on fees, billing and codes.
          </p>
        </div>

        {/* Sync result banner */}
        {syncResult && (
          <div data-testid="mbs-sync-result"
            className="mb-6 rounded-xl border p-4 flex items-start justify-between gap-3"
            style={syncResult.status === 'success'
              ? { borderColor: '#16A34A40', backgroundColor: '#16A34A10' }
              : { borderColor: '#DC262640', backgroundColor: '#DC262610' }}>
            <div className="flex items-start gap-2.5">
              {syncResult.status === 'success'
                ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#16A34A' }} />
                : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#DC2626' }} />}
              <div className="text-sm">
                {syncResult.status === 'success' ? (
                  <>
                    <p className="font-semibold text-foreground">
                      MBS sync complete — {syncResult.items_fetched} items pulled from MBS Online.
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      <strong className="text-foreground">{syncResult.items_added}</strong> new ·
                      <strong className="text-foreground"> {syncResult.items_changed}</strong> fee changes ·
                      <strong className="text-foreground"> {syncResult.items_unchanged}</strong> unchanged
                      {syncResult.last_sync && <> · {new Date(syncResult.last_sync).toLocaleString()}</>}
                    </p>
                    {syncResult.xml_url && (
                      <a href={syncResult.xml_url} target="_blank" rel="noreferrer"
                        className="inline-block mt-1 text-[11px] underline text-muted-foreground hover:text-foreground">
                        View source XML
                      </a>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-foreground">MBS sync failed</p>
                    <p className="text-xs text-muted-foreground mt-0.5 break-words">{syncResult.error || 'Check network connectivity to www.mbsonline.gov.au.'}</p>
                  </>
                )}
              </div>
            </div>
            <button onClick={() => setSyncResult(null)} data-testid="mbs-sync-result-dismiss"
              className="text-xs text-muted-foreground hover:text-foreground shrink-0">Dismiss</button>
          </div>
        )}

        {/* Alert Config Modal */}
        {showConfig && (
          <div className="mb-8 bg-background border-2 border-[#42CDD7]/30 rounded-xl p-6" data-testid="mbs-config-panel">
            <h3 className="font-['Outfit'] text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#42CDD7]" /> Email Alert Configuration
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-foreground/80 mb-1">Practice Manager Email</label>
                <input
                  data-testid="mbs-pm-email"
                  type="email" value={config.practice_manager_email}
                  onChange={e => setConfig({...config, practice_manager_email: e.target.value})}
                  placeholder="pm@practice.com.au"
                  className="w-full px-3 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30 focus:border-[#42CDD7]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground/80 mb-1">Key User Email</label>
                <input
                  data-testid="mbs-ku-email"
                  type="email" value={config.key_user_email}
                  onChange={e => setConfig({...config, key_user_email: e.target.value})}
                  placeholder="keyuser@practice.com.au"
                  className="w-full px-3 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30 focus:border-[#42CDD7]"
                />
              </div>
            </div>
            <button data-testid="mbs-save-config" onClick={handleSaveConfig} className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors hover:opacity-90" style={{ backgroundColor: G.color }}>
              Save Configuration
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-border">
          {[
            { id: 'dashboard', label: 'Fee Items' },
            { id: 'changes', label: `Changes (${changes.length})` },
            { id: 'alerts', label: `Alerts (${alerts.length})` },
            { id: 'upload', label: 'Upload Schedule' }
          ].map(t => (
            <button
              key={t.id} data-testid={`mbs-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
              style={tab === t.id ? { borderColor: G.color, color: G.color } : { borderColor: 'transparent', color: 'hsl(var(--muted-foreground))' }}
            >{t.label}</button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'dashboard' && (
          <div className="border border-border rounded-xl overflow-hidden" data-testid="mbs-items-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-muted border-b border-border">
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/80 uppercase">Item #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/80 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/80 uppercase">Category</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/80 uppercase">Schedule Fee</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/80 uppercase">Previous</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-foreground/80 uppercase">Status</th>
              </tr></thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-12 text-center text-muted-foreground">No MBS items loaded. Use the Upload tab to import a schedule.</td></tr>
                )}
                {items.map((item, i) => (
                  <tr key={i} className={`border-b border-border ${item.change_detected ? 'bg-amber-50' : i%2===0 ? 'bg-background' : 'bg-muted/50'}`}>
                    <td className="px-4 py-3 font-mono font-medium text-foreground">{item.item_number}</td>
                    <td className="px-4 py-3 text-foreground/80">{item.description}</td>
                    <td className="px-4 py-3 text-muted-foreground">{item.category}</td>
                    <td className="px-4 py-3 text-right font-medium text-foreground">${item.schedule_fee?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{item.previous_fee != null ? `$${item.previous_fee.toFixed(2)}` : '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {item.change_detected ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                          {item.schedule_fee > (item.previous_fee || 0) ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} Changed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                          <CheckCircle2 className="w-3 h-3" /> Current
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'changes' && (
          <div className="space-y-3" data-testid="mbs-changes">
            {changes.length === 0 && <p className="text-center py-12 text-muted-foreground">No fee changes detected</p>}
            {changes.map((c, i) => (
              <div key={i} className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div>
                  <span className="font-mono font-bold text-foreground">{c.item_number}</span>
                  <span className="ml-3 text-sm text-foreground/80">{c.description}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground line-through">${c.previous_fee?.toFixed(2)}</span>
                  <span className="font-bold text-[#42CDD7]">${c.schedule_fee?.toFixed(2)}</span>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${c.schedule_fee > (c.previous_fee||0) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {c.schedule_fee > (c.previous_fee||0) ? '+' : ''}{((c.schedule_fee - (c.previous_fee||0))/(c.previous_fee||1)*100).toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'alerts' && (
          <div className="space-y-3" data-testid="mbs-alerts">
            {alerts.length === 0 && <p className="text-center py-12 text-muted-foreground">No alerts yet</p>}
            {alerts.map((a, i) => (
              <div key={i} className="p-4 border border-border rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Bell className="w-4 h-4 text-[#42CDD7]" /> Fee Change Alert
                  </span>
                  <span className="text-xs text-muted-foreground">{new Date(a.detected_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-foreground/80">{a.items_changed} item(s) changed</p>
                {a.details?.slice(0, 5).map((d, j) => (
                  <div key={j} className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span className="font-mono">{d.item}</span>
                    <span className="line-through">${d.old?.toFixed(2)}</span>
                    <span className="font-bold text-[#42CDD7]">${d.new?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {tab === 'upload' && (
          <div className="bg-muted border border-border rounded-xl p-6" data-testid="mbs-upload">
            <h3 className="font-['Outfit'] text-base font-semibold text-foreground mb-2 flex items-center gap-2">
              <Upload className="w-4 h-4 text-[#42CDD7]" /> Upload MBS Schedule
            </h3>
            <p className="text-xs text-muted-foreground mb-4">Paste CSV data: item_number, description, schedule_fee, category (one per line)</p>
            <textarea
              data-testid="mbs-bulk-input"
              value={bulkInput} onChange={e => setBulkInput(e.target.value)}
              placeholder={"55028, CT Brain without contrast, 260.00, CT\n55029, CT Brain with contrast, 350.00, CT\n55812, US Abdomen, 120.50, US"}
              rows={10}
              className="w-full px-4 py-3 text-sm font-mono bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30 focus:border-[#42CDD7] resize-y"
            />
            <button
              data-testid="mbs-upload-btn"
              onClick={handleBulkUpload} disabled={loading || !bulkInput.trim()}
              className="mt-3 px-5 py-2.5 text-sm font-medium text-white disabled:bg-slate-300 rounded-lg transition-colors hover:opacity-90"
              style={{ backgroundColor: loading || !bulkInput.trim() ? undefined : G.color }}
            >
              {loading ? 'Uploading...' : 'Upload & Compare'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
